import { ElementType } from './element-type';
import { ReportElement } from './report-element';
import { ReportUnits } from './report-units';
import { ReportPaperSize } from './report-paper-size';
import { ReportPageMargin } from './report-page-margin';
import { ReportCommonSection } from './report-common-section';
import { TemplateSummary, TableColumn, Options, Validation } from './report-template';
import { ReportPreviewMetadata, PropertyPreviewMetadata, FieldType, UpdateMetadata } from '../_services/report-template-design.service';
import { Subscription } from 'rxjs';
import { DYNAMIC_SELECT_ITEM, BORDER_STYLE, FONT_FAMILY, FONT_WEIGHT, FONT_STYLE, INPUT_TYPES } from '../app.constants';
import { ReportFonts } from './report-fonts';
import { TranslateService } from '@ngx-translate/core';
import { LangUpdateService } from '../_services/lang-update.service';

export class ReportSummary extends ReportElement {
  public defaultPageBreakRowCount: number;
  public isEmptyFolderDisplayed: boolean;
  public hasInspectionSummaryLink: boolean;

  //public table: TemplateSummaryTable;
  public tableTop: string;
  public tableLeft: string;
  public tableWidth: string;
  public tableHeight: string;
  public tableRowHeight: string;
  public tableZIndex: number;
  public tableHeaderHeight: string;
  public tableHeaderBackgroundColor: string;
  public tableHeaderFontSize: string;
  public tableHeaderFontFamily: string;
  public tableHeaderHorizontalAlign: string;
  public tableIsHorizontal: boolean;
  public tableBorderWidth: string;
  public tableBorderStyle: string;
  public tableBorderColor: string;
  public tableHasBorderTop: boolean;
  public tableHasBorderBottom: boolean;
  public tableHasBorderLeft: boolean;
  public tableHasBorderRight: boolean;
  public tableHasRowBackground: boolean;
  public tableRowBackgroundColor: string;
  public tableColumns: TableColumn[];
  public tableResize: boolean;
  public tableTopValue: number;
  public tableLeftValue: number;
  public tableMode: boolean;
  public dataMode: number;

  //public title: TemplateText;
  public titleTop: string;
  public titleLeft: string;
  public titleWidth: string;
  public titleHeight: string;
  public titlePadding: string;
  public titlePaddingLeft: string;
  public titlePaddingBottom: string;
  public titlePaddingRight: string;
  public titlePaddingTop: string;
  public titleZIndex: number;
  public titleText: string;
  public titleFontSize: string;
  public titleFontFamily: string;
  public titleFontColor: string;
  public titleFontStyle: string;
  public titleFontWeight: string;
  public titleLineHeight: string;
  public titleTextAlign: string;
  public titleVerticalAlign: string;
  public titleBorderWidth: string;
  public titleBorderStyle: string;
  public titleBorderColor: string;
  public titleBackgroundColor: string;
  public titleIsHorizontal: boolean;
  public titleJustifyContent: string;
  public titleAlignItems: string;

  public titleType: string;
  public titleFormat: string;
  public titleDynamicId: string;
  public titleInputType: string;
  public titleEditableId: string;
  public titleValidation: Validation;
  public titleRadioType: string;
  public titleRadioLabel: string;
  public titleOptions: Options[];
  public titleMultiSelectDelimiter: string;
  public titleLabel: string;
  public titleValue: string;
  public titleInputGroup: string;

  private units: ReportUnits;
  private fonts: ReportFonts;
  private paperSize: ReportPaperSize;
  private pageMargin: ReportPageMargin;
  private header: ReportCommonSection;
  private footer: ReportCommonSection;

  private baseDef: TemplateSummary;
  private readonly metaDefaultPageBreakRowCount = 'defaultPageBreakRowCount';
  private readonly metaIsEmptyFolderDisplayed = 'isEmptyFolderDisplayed';
  private readonly metaHasInspectionSummaryLink = 'hasInspectionSummaryLink';

  private readonly metaTable = 'table';
  private readonly metaTitle = 'title';

  private readonly metaId = 'id';
  private readonly metaTop = 'y';
  private readonly metaLeft = 'x';
  private readonly metaWidth = 'width';
  private readonly metaHeight = 'height';
  private readonly metaZIndex = 'zIndex';
  private readonly metaBorderWidth = 'borderWidth';
  private readonly metaBorderStyle = 'borderStyle';
  private readonly metaBorderColor = 'borderColor';
  private readonly metaBGColor = 'backgroundColor';
  private readonly metaFontSize = 'fontSize';
  private readonly metaFontFamily = 'fontFamily';
  private readonly metaFontStyle = 'fontStyle';
  private readonly metaFontWeight = 'fontWeight';
  private readonly metaFontColor = 'fontColor';
  private readonly metaTextAlign = 'horizontalAlign';
  private readonly metaVerticalAlign = 'verticalAlign';
  private readonly metaLineHeight = 'lineHeight';
  private readonly metaPaddingLeft = 'paddingLeft';
  private readonly metaPaddingBottom = 'paddingBottom';
  private readonly metaPaddingRight = 'paddingRight';
  private readonly metaPaddingTop = 'paddingTop';
  private readonly metaIsHorizontal = 'isHorizontal';

  private readonly metaText = 'text';
  private readonly metaType = 'type';
  private readonly metaDynamicId = 'dynamicId';
  private readonly metaEditableId = 'editableId';
  private readonly metaInputType = 'inputType';
  private readonly metaValidation = 'validation';
  private readonly metaFormat = 'format';
  private readonly metaRequired = 'validation.required';
  private readonly metaMaxLength = 'validation.maxLength';
  private readonly metaRadioType = 'radioType';
  private readonly metaRadioLabel = 'radioLabel';
  private readonly metaOptions = 'options';
  private readonly metaMultiSelectDelimiter = 'multiSelectDelimiter';
  private readonly metaLabel = 'label';
  private readonly metaValue = 'value';
  private readonly metaInputGroup = 'inputGroup';

  private readonly metaTableHeaderHeight = 'headerHeight';
  private readonly metaTableHeaderBackgroundColor = 'headerBackgroundColor';
  private readonly metaTableHeaderFontSize = 'headerFontSize';
  private readonly metaTableHeaderFontFamily = 'headerFontFamily';
  private readonly metaTableHorizontalAlign = 'horizontalAlign';
  private readonly metaTableBorderWidth = 'borderWidth';
  private readonly metaTableBorderStyle = 'borderStyle';
  private readonly metaTableBorderColor = 'borderColor';
  private readonly metaTableHasBorderTop = 'hasBorderTop';
  private readonly metaTableHasBorderBottom = 'hasBorderBottom';
  private readonly metaTableHasBorderLeft = 'hasBorderLeft';
  private readonly metaTableHasBorderRight = 'hasBorderRight';
  private readonly metaTableHasRowBackground = 'hasRowBackground';
  private readonly metaTableRowBackgroundColor = 'rowBackgroundColor';
  private readonly metaTableColumns = 'columns';
  private readonly metaDataMode = 'dataMode';

  constructor(
    type: ElementType, index: number, units: ReportUnits, fonts: ReportFonts, header: ReportCommonSection, footer: ReportCommonSection,
    paperSize: ReportPaperSize, pageMargin: ReportPageMargin,
    protected translate: TranslateService,
    protected langService: LangUpdateService,
    templateSummary?: TemplateSummary
  ) {

    super(type, index, !(templateSummary == null), translate, langService, templateSummary ? templateSummary.id : null);

    this.units = units;
    this.fonts = fonts;
    this.header = header;
    this.footer = footer;
    this.paperSize = paperSize;
    this.pageMargin = pageMargin;

    const title = {
      id: this.generateUUID(),
      x: 0, // 初期値を0で仮設定
      y: 0, // 初期値を0で仮設定
      width: Math.floor(this.pageMargin.getPaddedWidthNum(this.paperSize.getWidthNum())), // 初期値を0で仮設定
      height: Math.floor(this.pageMargin.getPaddedHeightNum(this.paperSize.getHeightNum())), // 初期値を0で仮設定
      zIndex: 0, // 初期値を0で仮設定
      borderColor: null,
      borderStyle: null,
      borderWidth: null,
      backgroundColor: null,
      type: 'static', // 初期値をstaticで仮設定
      text: null,
      format: null,
      fontSize: this.fonts.getInitailFontSize(),
      fontFamily: this.fonts.getInitialFontFamily(),
      fontColor: null,
      fontStyle: null,
      fontWeight: null,
      horizontalAlign: 'left', // 初期値をleftで仮設定
      verticalAlign: 'middle', // 初期値をcenterで仮設定
      isHorizontal: true, // 初期値をtrueで設定
      lineHeight: null,
      dynamicId: null,
      dynamicDataField: null, // 初期値をnullで仮設定
      inputType: 'text', // 初期値をtextで仮設定
      editableId: null,
      validation: {
        required: false,
        maxLength: 100,
      },
      radioType: null,
      radioLabel: null,
      options: [],
      label: null,
      value: null,
      inputGroup: null,
      padding: null,
      paddingRight: 0.5,
      paddingBottom: 0.5,
      paddingLeft: 0.5,
      paddingTop: 0.5,
      multiSelectDelimiter: 'comma'
    };
    const table = {
      id: this.generateUUID(),
      x: 0, // 初期値を0で仮設定
      y: 0, // 初期値を0で仮設定
      width: Math.floor(this.pageMargin.getPaddedWidthNum(this.paperSize.getWidthNum())), // 初期値を0で仮設定
      height: Math.floor(this.pageMargin.getPaddedHeightNum(this.paperSize.getHeightNum())), // 初期値を0で仮設定
      zIndex: 0, // 初期値を0で仮設定
      headerHeight: 10, // 初期値を0で仮設定
      headerBackgroundColor: null,
      headerFontSize: this.fonts.getInitailFontSize(),
      headerFontFamily: this.fonts.getInitialFontFamily(),
      horizontalAlign: 'left', // 初期値をleftで仮設定
      borderColor: null,
      borderStyle: null,
      borderWidth: null,
      hasBorderTop: true,
      hasBorderBottom: true,
      hasBorderLeft: true,
      hasBorderRight: true,
      hasRowBackground: true,
      rowBackgroundColor: null,
      columns: Array<TableColumn>(),
      tableMode: false,
      dataMode: 0
    };

    if (templateSummary) {
      this.baseDef = templateSummary;

    } else {
      this.baseDef = {
        id: this.generateUUID(),
        x: 0, // 初期値を0で仮設定
        y: 0, // 初期値を0で仮設定
        width: 0, // 初期値を0で仮設定
        height: 0, // 初期値を0で仮設定
        zIndex: 0, // 初期値を0で仮設定
        defaultPageBreakRowCount: 0,
        isEmptyFolderDisplayed: false,
        hasInspectionSummaryLink: true,
        title: title,
        table: table
      };
    }
    console.log('summary table');
    console.log(this.baseDef.title);
    console.log(this.baseDef.table);

    this.defaultPageBreakRowCount = this.baseDef.defaultPageBreakRowCount;
    this.isEmptyFolderDisplayed = this.baseDef.isEmptyFolderDisplayed;
    this.hasInspectionSummaryLink = this.baseDef.hasInspectionSummaryLink;
    // title
    this.titleTop = this.units.concatenate('sizeUnit', this.baseDef.title.y);
    this.titleLeft = this.units.concatenate('sizeUnit', this.baseDef.title.x);
    this.titleWidth = this.units.concatenate('sizeUnit', this.baseDef.title.width);
    this.titleHeight = this.units.concatenate('sizeUnit', this.baseDef.title.height);
    this.titleZIndex = this.baseDef.title.zIndex;
    this.titleFontColor = this.baseDef.title.fontColor;
    this.titleFontFamily = this.baseDef.title.fontFamily;
    this.titleFontSize = this.units.concatenate('fontSizeUnit', this.baseDef.title.fontSize);
    this.titleFontStyle = this.baseDef.title.fontStyle;
    this.titleFontWeight = this.baseDef.title.fontWeight;
    this.titleVerticalAlign = this.baseDef.title.verticalAlign;
    this.titleTextAlign = this.baseDef.title.horizontalAlign;
    this.titleLineHeight = this.units.concatenate('sizeUnit', this.baseDef.title.lineHeight);
    this.titleBackgroundColor = this.baseDef.title.backgroundColor;
    this.titleBorderColor = this.baseDef.title.borderColor;
    this.titleBorderStyle = this.baseDef.title.borderStyle;
    this.titleBorderWidth = this.units.concatenate('sizeUnit', this.baseDef.title.borderWidth);
    this.titleText = this.getText();
    this.titleJustifyContent = this.getJustifyContent();
    this.titleAlignItems = this.getAlignItems();
    this.titleType = this.baseDef.title.type;
    this.titleFormat = this.baseDef.title.format;
    this.titleDynamicId = this.baseDef.title.dynamicId;
    this.titleInputType = this.baseDef.title.inputType;
    this.titleEditableId = this.baseDef.title.editableId;
    this.titleValidation = this.baseDef.title.validation;
    this.titleRadioType = this.baseDef.title.radioType;
    this.titleRadioLabel = this.baseDef.title.radioLabel;
    this.titleOptions = this.baseDef.title.options;
    this.titleMultiSelectDelimiter = this.baseDef.title.multiSelectDelimiter;
    this.titleLabel = this.baseDef.title.label;
    this.titleValue = this.baseDef.title.value;
    this.titleInputGroup = this.baseDef.title.inputGroup;

    //table
    this.tableTop = this.units.concatenate('sizeUnit', this.baseDef.table.y);
    this.tableLeft = this.units.concatenate('sizeUnit', this.baseDef.table.x);
    this.tableWidth = this.units.concatenate('sizeUnit', this.baseDef.table.width);
    if (this.baseDef.table.height === 0) {
      this.tableHeight = this.units.concatenate('sizeUnit', this.getHeightNum('table'));
      this.tableRowHeight = this.units.concatenate('sizeUnit', this.getHeightNum('table') / this.baseDef.defaultPageBreakRowCount);
    } else {
      this.tableHeight = this.units.concatenate('sizeUnit', this.baseDef.table.height);
      this.tableRowHeight = this.units.concatenate('sizeUnit', this.baseDef.table.height / this.baseDef.defaultPageBreakRowCount);
    }
    this.tableZIndex = this.baseDef.table.zIndex;
    this.tableHeaderHeight = this.units.concatenate('sizeUnit', this.baseDef.table.headerHeight);
    this.tableHeaderFontSize = this.units.concatenate('fontSizeUnit', this.baseDef.table.headerFontSize);
    this.tableHeaderFontFamily = this.baseDef.table.headerFontFamily;
    this.tableHeaderHorizontalAlign = this.baseDef.table.horizontalAlign;
    this.tableHeaderBackgroundColor = this.baseDef.table.headerBackgroundColor;
    this.tableRowBackgroundColor = this.baseDef.table.rowBackgroundColor;
    this.tableBorderStyle = this.baseDef.table.borderStyle;
    this.tableBorderWidth = this.units.concatenate('sizeUnit', this.baseDef.table.borderWidth);
    this.tableBorderColor = this.baseDef.table.borderColor;
    this.tableHasBorderTop = this.baseDef.table.hasBorderTop;
    this.tableHasBorderBottom = this.baseDef.table.hasBorderBottom;
    this.tableHasBorderLeft = this.baseDef.table.hasBorderLeft;
    this.tableHasBorderRight = this.baseDef.table.hasBorderRight;
    this.tableHasRowBackground = this.baseDef.table.hasRowBackground;
    this.tableColumns = this.baseDef.table.columns;
    this.tableResize = false;
    this.tableMode = this.baseDef.table.tableMode;
    this.dataMode = this.baseDef.table.dataMode;

    this.setChainUpdate(); // プロパティの連鎖更新処理の登録
    this.setPreviewMetadata(); // プロパティプレビュー用メタデータの準備
  }

  public setDrawData(metadata: ReportPreviewMetadata) {
    this.updateProperties({
      id: this.getId(),
      name: this.metaTop,
      parentName: this.metaTitle,
      value: this.units.fromPx('sizeUnit', metadata.topPx),
      oldValue: this.baseDef[this.metaTitle][this.metaTop]
    });

    this.updateProperties({
      id: this.getId(),
      name: this.metaLeft,
      parentName: this.metaTitle,
      value: this.units.fromPx('sizeUnit', metadata.leftPx),
      oldValue: this.baseDef[this.metaTitle][this.metaLeft]
    });

    this.updateProperties({
      id: this.getId(),
      name: this.metaWidth,
      parentName: this.metaTitle,
      value: this.units.fromPx('sizeUnit', metadata.widthPx),
      oldValue: this.baseDef[this.metaTitle][this.metaWidth]
    });

    this.updateProperties({
      id: this.getId(),
      name: this.metaHeight,
      parentName: this.metaTitle,
      value: this.units.fromPx('sizeUnit', metadata.heightPx / 5),
      oldValue: this.baseDef[this.metaTitle][this.metaHeight]
    });

    this.updateProperties({
      id: this.getId(),
      name: this.metaTop,
      parentName: this.metaTable,
      value: this.units.fromPx('sizeUnit', metadata.topPx + metadata.heightPx / 5),
      oldValue: this.baseDef[this.metaTable][this.metaTop]
    });

    this.updateProperties({
      id: this.getId(),
      name: this.metaLeft,
      parentName: this.metaTable,
      value: this.units.fromPx('sizeUnit', metadata.leftPx),
      oldValue: this.baseDef[this.metaTable][this.metaLeft]
    });

    this.updateProperties({
      id: this.getId(),
      name: this.metaWidth,
      parentName: this.metaTable,
      value: this.units.fromPx('sizeUnit', metadata.widthPx),
      oldValue: this.baseDef[this.metaTable][this.metaWidth]
    });

    this.updateProperties({
      id: this.getId(),
      name: this.metaHeight,
      parentName: this.metaTable,
      value: this.units.fromPx('sizeUnit', metadata.heightPx - metadata.heightPx / 5),
      oldValue: this.baseDef[this.metaTable][this.metaHeight]
    });
  }
  public setDropData(metadata: ReportPreviewMetadata) {
    this.tableTopValue = Math.round((this.units.fromPx('sizeUnit', metadata.topPx) + this.baseDef[this.metaTable][this.metaTop] - this.baseDef[this.metaTitle][this.metaTop]) * 10) / 10;
    this.tableLeftValue = Math.round((this.units.fromPx('sizeUnit', metadata.leftPx) + this.baseDef[this.metaTable][this.metaLeft] - this.baseDef[this.metaTitle][this.metaLeft]) * 10) / 10;
    this.updateProperties({
      id: this.getId(),
      name: this.metaTop,
      parentName: this.metaTitle,
      value: this.units.fromPx('sizeUnit', metadata.topPx),
      oldValue: this.baseDef[this.metaTitle][this.metaTop]
    });
    this.updateProperties({
      id: this.getId(),
      name: this.metaLeft,
      parentName: this.metaTitle,
      value: this.units.fromPx('sizeUnit', metadata.leftPx),
      oldValue: this.baseDef[this.metaTitle][this.metaLeft]
    });
    this.updateProperties({
      id: this.getId(),
      name: this.metaTop,
      parentName: this.metaTable,
      value: this.tableTopValue,
      oldValue: this.baseDef[this.metaTable][this.metaTop]
    });
    this.updateProperties({
      id: this.getId(),
      name: this.metaLeft,
      parentName: this.metaTable,
      value: this.tableLeftValue,
      oldValue: this.baseDef[this.metaTable][this.metaLeft]
    });
  }
  public setResizeData(metadata: ReportPreviewMetadata) {
    if (this.tableResize) {
      this.updateProperties({
        id: this.getId(),
        name: this.metaTop,
        parentName: this.metaTable,
        value: this.units.fromPx('sizeUnit', metadata.topPx),
        oldValue: this.baseDef[this.metaTable][this.metaTop]
      });
      this.updateProperties({
        id: this.getId(),
        name: this.metaLeft,
        parentName: this.metaTable,
        value: this.units.fromPx('sizeUnit', metadata.leftPx),
        oldValue: this.baseDef[this.metaTable][this.metaLeft]
      });
      this.updateProperties({
        id: this.getId(),
        name: this.metaWidth,
        parentName: this.metaTable,
        value: this.units.fromPx('sizeUnit', metadata.widthPx),
        oldValue: this.baseDef[this.metaTable][this.metaWidth]
      });
      this.updateProperties({
        id: this.getId(),
        name: this.metaHeight,
        parentName: this.metaTable,
        value: this.units.fromPx('sizeUnit', metadata.heightPx),
        oldValue: this.baseDef[this.metaTable][this.metaHeight]
      });
    }
    else {
      this.updateProperties({
        id: this.getId(),
        name: this.metaTop,
        parentName: this.metaTitle,
        value: this.units.fromPx('sizeUnit', metadata.topPx),
        oldValue: this.baseDef[this.metaTitle][this.metaTop]
      });
      this.updateProperties({
        id: this.getId(),
        name: this.metaLeft,
        parentName: this.metaTitle,
        value: this.units.fromPx('sizeUnit', metadata.leftPx),
        oldValue: this.baseDef[this.metaTitle][this.metaLeft]
      });
      this.updateProperties({
        id: this.getId(),
        name: this.metaWidth,
        parentName: this.metaTitle,
        value: this.units.fromPx('sizeUnit', metadata.widthPx),
        oldValue: this.baseDef[this.metaTitle][this.metaWidth]
      });
      this.updateProperties({
        id: this.getId(),
        name: this.metaHeight,
        parentName: this.metaTitle,
        value: this.units.fromPx('sizeUnit', metadata.heightPx),
        oldValue: this.baseDef[this.metaTitle][this.metaHeight]
      });
    }
  }

  public getLeftPx(): number {
    if (this.tableResize) {
      return this.units.toPx('sizeUnit', this.baseDef[this.metaTable][this.metaLeft]);
    }
    else {
      return this.units.toPx('sizeUnit', this.baseDef[this.metaTitle][this.metaLeft]);
    }
  }
  public getTopPx(): number {
    if (this.tableResize) {
      return this.units.toPx('sizeUnit', this.baseDef[this.metaTable][this.metaTop]);
    }
    else {
      return this.units.toPx('sizeUnit', this.baseDef[this.metaTitle][this.metaTop]);
    }
  }
  public getWidthPx(): number {
    if (this.tableResize) {
      return this.units.toPx('sizeUnit', this.baseDef[this.metaTable][this.metaWidth]);
    }
    else {
      return this.units.toPx('sizeUnit', this.baseDef[this.metaTitle][this.metaWidth]);
    }
  }
  public getHeightPx(): number {
    if (this.tableResize) {
      return this.units.toPx('sizeUnit', this.baseDef[this.metaTable][this.metaHeight]);
    }
    else {
      return this.units.toPx('sizeUnit', this.baseDef[this.metaTitle][this.metaHeight]);
    }
  }
  public getTitleWidthPx(): number {
    return this.units.toPx('sizeUnit', this.baseDef[this.metaTitle][this.metaWidth]);
  }
  public getTitleHeightPx(): number {
    return this.units.toPx('sizeUnit', this.baseDef[this.metaTitle][this.metaHeight]);
  }
  public getTableWidthPx(): number {
    return this.units.toPx('sizeUnit', this.baseDef[this.metaTable][this.metaWidth]);
  }
  public getTableHeightPx(): number {
    return this.units.toPx('sizeUnit', this.baseDef[this.metaTable][this.metaHeight]);
  }

  public getTitleTopNum(): number {
    return this.baseDef.title.y;
  }

  /**
   * テンプレートJSONを返却する
   */
  public getTemplateJSON(): TemplateSummary {
    return this.baseDef;
  }

  /**
   * プロパティ一覧のメタデータを返却する
   */
  protected getPreviewMetadata(): PropertyPreviewMetadata[] {
    return [
      // General
      {
        name: this.metaDefaultPageBreakRowCount, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        isDisplay: true,
        category: 'GENERAL'
      },
      {
        name: this.metaIsEmptyFolderDisplayed, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'CheckBox',
        selectItems: [],
        isRequired: false,
        isEdited: false,
        isDisplay: true,
        category: 'GENERAL'
      },
      {
        name: this.metaHasInspectionSummaryLink, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'CheckBox',
        selectItems: [],
        isRequired: false,
        isEdited: false,
        isDisplay: true,
        category: 'GENERAL'
      },
      {
        name: this.metaLeft, parentName: this.metaTitle, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        isDisplay: true,
        group: 'Position',
        category: 'TITLE'
      },
      {
        name: this.metaTop, parentName: this.metaTitle, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        isDisplay: true,
        group: 'Position',
        category: 'TITLE'
      },
      {
        name: this.metaZIndex, parentName: this.metaTitle, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Integer',
        selectItems: [],
        isRequired: false,
        isEdited: false,
        isDisplay: true,
        group: 'Position',
        category: 'TITLE'
      },
      {
        name: this.metaHeight, parentName: this.metaTitle, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        isDisplay: true,
        group: 'Size',
        category: 'TITLE'
      },
      {
        name: this.metaWidth, parentName: this.metaTitle, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        isDisplay: true,
        group: 'Size',
        category: 'TITLE'
      },
      {
        name: this.metaPaddingLeft, parentName: this.metaTitle, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: false,
        isEdited: false,
        isDisplay: true,
        group: 'TextBox',
        category: 'TITLE'
      },
      {
        name: this.metaPaddingBottom, parentName: this.metaTitle, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: false,
        isEdited: false,
        isDisplay: true,
        group: 'TextBox',
        category: 'TITLE'
      },
      {
        name: this.metaPaddingRight, parentName: this.metaTitle, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: false,
        isEdited: false,
        isDisplay: true,
        group: 'TextBox',
        category: 'TITLE'
      },
      {
        name: this.metaPaddingTop, parentName: this.metaTitle, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: false,
        isEdited: false,
        isDisplay: true,
        group: 'TextBox',
        category: 'TITLE'
      },
      {
        name: this.metaVerticalAlign, parentName: this.metaTitle, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Select',
        selectItems: [
          { label: 'top', value: 'top' },
          { label: 'middle', value: 'middle' },
          { label: 'bottom', value: 'bottom' },
        ],
        isRequired: false,
        isEdited: false,
        isDisplay: true,
        group: 'TextBox',
        category: 'TITLE'
      },
      {
        name: this.metaTextAlign, parentName: this.metaTitle, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Select',
        selectItems: [
          { label: 'left', value: 'left' },
          { label: 'center', value: 'center' },
          { label: 'right', value: 'right' },
        ],
        isRequired: false,
        isEdited: false,
        isDisplay: true,
        group: 'TextBox',
        category: 'TITLE'
      },
      {
        name: this.metaLineHeight, parentName: this.metaTitle, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        // ↑ CSSプロパティ「lineHight」は文字列値も受け付けられたような。。。
        selectItems: [],
        isRequired: false,
        isEdited: false,
        isDisplay: true,
        group: 'TextBox',
        category: 'TITLE'
      },
      {
        name: this.metaBorderStyle, parentName: this.metaTitle, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Select',
        selectItems: BORDER_STYLE,
        isRequired: false,
        isEdited: false,
        isDisplay: true,
        group: 'Line',
        category: 'TITLE'
      },
      {
        name: this.metaBorderWidth, parentName: this.metaTitle, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: false,
        isEdited: false,
        isDisplay: true,
        group: 'Line',
        category: 'TITLE'
      },
      {
        name: this.metaBorderColor, parentName: this.metaTitle, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Color',
        selectItems: [],
        isRequired: false,
        isEdited: false,
        isDisplay: true,
        group: 'Line',
        category: 'TITLE'
      },
      {
        name: this.metaBGColor, parentName: this.metaTitle, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Color',
        selectItems: [],
        isRequired: false,
        isEdited: false,
        isDisplay: true,
        group: 'Background',
        category: 'TITLE'
      },
      {
        name: this.metaFontSize, parentName: this.metaTitle, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: false,
        isEdited: false,
        isDisplay: true,
        group: 'Font',
        category: 'TITLE'
      },
      {
        name: this.metaFontFamily, parentName: this.metaTitle, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Select',
        selectItems: FONT_FAMILY,
        isRequired: false,
        isEdited: false,
        isDisplay: true,
        group: 'Font',
        category: 'TITLE'
      },
      {
        name: this.metaFontColor, parentName: this.metaTitle, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Color',
        selectItems: [],
        isRequired: false,
        isEdited: false,
        isDisplay: true,
        group: 'Font',
        category: 'TITLE'
      },
      {
        name: this.metaFontStyle, parentName: this.metaTitle, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Select',
        selectItems: FONT_STYLE,
        isRequired: false,
        isEdited: false,
        isDisplay: true,
        group: 'Font',
        category: 'TITLE'
      },
      {
        name: this.metaFontWeight, parentName: this.metaTitle, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Select',
        selectItems: FONT_WEIGHT,
        isRequired: false,
        isEdited: false,
        isDisplay: true,
        group: 'Font',
        category: 'TITLE'
      },
      {
        name: this.metaType, parentName: this.metaTitle, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Select',
        selectItems: [
          { label: 'static', value: 'static' },
          { label: 'dynamic', value: 'dynamic' },
          { label: 'dynamic-editable', value: 'dynamic-editable' },
        ],
        isRequired: true,
        isEdited: false,
        isDisplay: true,
        group: 'DataBinding',
        category: 'TITLE'
      },
      {
        name: this.metaInputType, parentName: this.metaTitle, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Select',
        selectItems: INPUT_TYPES,
        isRequired: true,
        isEdited: false,
        isDisplay: true,
        group: 'DataBinding',
        category: 'TITLE'
      },
      {
        name: this.metaText, parentName: this.metaTitle, value: '',
        isFieldType: (type: FieldType): boolean => type === 'TextArea',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        isDisplay: true,
        group: 'DataBinding',
        category: 'TITLE'
      },
      {
        name: this.metaDynamicId, parentName: this.metaTitle, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Select',
        selectItems: DYNAMIC_SELECT_ITEM,
        isRequired: false,
        isEdited: false,
        isDisplay: true,
        group: 'DataBinding',
        category: 'TITLE'
      },
      {
        name: this.metaFormat, parentName: this.metaTitle, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Select',
        selectItems: [
          { label: '01/01/2019', value: 'MM/dd/yyyy' },
          { label: '1/1/2019', value: 'M/d/yyyy' },
          { label: '1/1/19', value: 'M/d/yy' },
          { label: '2019/1/1', value: 'yyyy/M/d' },
          { label: '19/1/1', value: 'yy/M/d' },
          { label: '2019-01-01', value: 'yyyy-MM-dd' },
          { label: '2019-1-1', value: 'yyyy-M-d' },
          { label: '19-01-01', value: 'yy-MM-dd' },
          { label: 'January 1, 2019', value: 'MMMM d, yyyy' },
          { label: 'January 01, 2019', value: 'MMMM dd, yyyy' },
          { label: 'Tuesday, January 01, 2019', value: 'dddd, MMMM dd, yyyy' },
          { label: 'Tuesday, Jan 1, 2019', value: 'dddd, MMM d, yyy' },
          { label: 'Tues, Jan 1, 2019', value: 'ddd, MMM d, yyyy' },
          { label: '1/10', value: '$/#' },
          { label: '1', value: '$' },
          { label: 'p.1', value: 'p.$' },
          { label: 'Page 1 of 10', value: 'Page $ of #' },
        ],
        isRequired: false,
        isEdited: false,
        isDisplay: true,
        group: 'DataBinding',
        category: 'TITLE'
      },
      {
        name: this.metaMaxLength, parentName: this.metaTitle, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Integer',
        selectItems: [],
        isRequired: false,
        isEdited: false,
        isDisplay: true,
        group: 'DataBinding',
        category: 'TITLE'
      },
      {
        name: this.metaRequired, parentName: this.metaTitle, value: '',
        isFieldType: (type: FieldType): boolean => type === 'CheckBox',
        selectItems: [],
        isRequired: false,
        isEdited: false,
        isDisplay: true,
        group: 'DataBinding',
        category: 'TITLE'
      },
      {
        name: this.metaOptions, parentName: this.metaTitle, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Multi',
        selectItems: [],
        isRequired: false,
        isEdited: false,
        isDisplay: false,
        group: 'Select',
        category: 'TITLE'
      },
      {
        name: this.metaMultiSelectDelimiter, parentName: this.metaTitle, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Select',
        selectItems: [
          { label: 'comma', value: 'comma' },
          { label: 'new line', value: 'newline' }
        ],
        isRequired: false,
        isEdited: false,
        isDisplay: false,
        group: 'Select',
        category: 'TITLE'
      },
      {
        name: this.metaRadioType, parentName: this.metaTitle, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Select',
        selectItems: [
          { label: 'check', value: 'check' },
          { label: 'circle', value: 'circle' },
          { label: 'disc', value: 'disc' },
        ],
        isRequired: false,
        isEdited: false,
        isDisplay: false,
        group: 'Radio',
        category: 'TITLE'
      },
      {
        name: this.metaRadioLabel, parentName: this.metaTitle, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Text',
        selectItems: [],
        isRequired: false,
        isEdited: false,
        isDisplay: false,
        group: 'Radio',
        category: 'TITLE'
      },
      {
        name: this.metaValue, parentName: this.metaTitle, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Text',
        selectItems: [],
        isRequired: false,
        isEdited: false,
        isDisplay: false,
        group: 'Radio',
        category: 'TITLE'
      },
      {
        name: this.metaEditableId, parentName: this.metaTitle, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Text',
        selectItems: [],
        isRequired: false,
        isEdited: false,
        isDisplay: true,
        group: 'Input',
        category: 'TITLE'
      },
      {
        name: this.metaLabel, parentName: this.metaTitle, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Text',
        selectItems: [],
        isRequired: false,
        isEdited: false,
        isDisplay: true,
        group: 'Input',
        category: 'TITLE'
      },
      {
        name: this.metaInputGroup, parentName: this.metaTitle, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Text',
        selectItems: [],
        isRequired: false,
        isEdited: false,
        isDisplay: true,
        group: 'Input',
        category: 'TITLE'
      },
      {
        name: this.metaLeft, parentName: this.metaTable, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        isDisplay: true,
        group: 'Position',
        category: 'TABLE'
      },
      {
        name: this.metaTop, parentName: this.metaTable, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        isDisplay: true,
        group: 'Position',
        category: 'TABLE'
      },
      {
        name: this.metaZIndex, parentName: this.metaTable, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Integer',
        selectItems: [],
        isRequired: false,
        isEdited: false,
        isDisplay: true,
        group: 'Position',
        category: 'TABLE'
      },
      {
        name: this.metaHeight, parentName: this.metaTable, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        isDisplay: true,
        group: 'Size',
        category: 'TABLE'
      },
      {
        name: this.metaWidth, parentName: this.metaTable, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        isDisplay: true,
        group: 'Size',
        category: 'TABLE'
      },
      {
        name: this.metaTableHeaderHeight, parentName: this.metaTable, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        isDisplay: true,
        group: 'Size',
        category: 'TABLE'
      },
      {
        name: this.metaTableHorizontalAlign, parentName: this.metaTable, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Select',
        selectItems: [
          { label: 'left', value: 'left' },
          { label: 'center', value: 'center' },
          { label: 'right', value: 'right' },
        ],
        isRequired: true,
        isEdited: false,
        isDisplay: true,
        group: 'TextBox',
        category: 'TABLE'
      },
      {
        name: this.metaTableHasBorderTop, parentName: this.metaTable, value: '',
        isFieldType: (type: FieldType): boolean => type === 'CheckBox',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        isDisplay: true,
        group: 'Line',
        category: 'TABLE'
      },
      {
        name: this.metaTableHasBorderBottom, parentName: this.metaTable, value: '',
        isFieldType: (type: FieldType): boolean => type === 'CheckBox',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        isDisplay: true,
        group: 'Line',
        category: 'TABLE'
      },
      {
        name: this.metaTableHasBorderLeft, parentName: this.metaTable, value: '',
        isFieldType: (type: FieldType): boolean => type === 'CheckBox',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        isDisplay: true,
        group: 'Line',
        category: 'TABLE'
      },
      {
        name: this.metaTableHasBorderRight, parentName: this.metaTable, value: '',
        isFieldType: (type: FieldType): boolean => type === 'CheckBox',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        isDisplay: true,
        group: 'Line',
        category: 'TABLE'
      },
      {
        name: this.metaTableBorderStyle, parentName: this.metaTable, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Select',
        selectItems: BORDER_STYLE,
        isRequired: false,
        isEdited: false,
        isDisplay: true,
        group: 'Line',
        category: 'TABLE'
      },
      {
        name: this.metaTableBorderWidth, parentName: this.metaTable, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: false,
        isEdited: false,
        isDisplay: true,
        group: 'Line',
        category: 'TABLE'
      },
      {
        name: this.metaTableBorderColor, parentName: this.metaTable, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Color',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        isDisplay: true,
        group: 'Line',
        category: 'TABLE'
      },
      {
        name: this.metaTableHeaderBackgroundColor, parentName: this.metaTable, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Color',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        isDisplay: true,
        group: 'Background',
        category: 'TABLE'
      },
      {
        name: this.metaTableHasRowBackground, parentName: this.metaTable, value: '',
        isFieldType: (type: FieldType): boolean => type === 'CheckBox',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        isDisplay: true,
        group: 'Background',
        category: 'TABLE'
      },
      {
        name: this.metaTableRowBackgroundColor, parentName: this.metaTable, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Color',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        isDisplay: true,
        group: 'Background',
        category: 'TABLE'
      },
      {
        name: this.metaTableHeaderFontSize, parentName: this.metaTable, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        isDisplay: true,
        group: 'Font',
        category: 'TABLE'
      },
      {
        name: this.metaTableHeaderFontFamily, parentName: this.metaTable, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Select',
        selectItems: FONT_FAMILY,
        isRequired: false,
        isEdited: false,
        isDisplay: true,
        group: 'Font',
        category: 'TABLE'
      },
      {
        name: this.metaDataMode, parentName: this.metaTable, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Select',
        selectItems: [
          { label: 'WorstOnly', value: 0 },
          { label: 'All', value: 1 },
        ],
        isRequired: true,
        isEdited: false,
        isDisplay: true,
        category: 'TABLE',
        group: 'DataBinding'
      },
      {
        name: this.metaTableColumns, parentName: this.metaTable, value: this.baseDef.table.columns === null ? null : JSON.stringify(this.baseDef.table.columns),
        isFieldType: (type: FieldType): boolean => type === 'TableColumn',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        isDisplay: true,
        category: 'TABLE',
        group: 'DataBinding'
      }
    ];
  }

  /**
   * 更新データを「テンプレートJSON／Angularオートバインディングプロパティ」に設定する
   * @param metadata 更新データ
   */
  protected setUpdateMetadata(metadata: UpdateMetadata) {
    switch (metadata.name) {
      case this.metaDefaultPageBreakRowCount:
        this.baseDef[metadata.name] = metadata.value as number;
        this.defaultPageBreakRowCount = metadata.value as number;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaIsEmptyFolderDisplayed:
        this.baseDef[metadata.name] = metadata.value as boolean;
        this.isEmptyFolderDisplayed = this.baseDef.isEmptyFolderDisplayed;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaHasInspectionSummaryLink:
        this.baseDef[metadata.name] = metadata.value as boolean;
        this.hasInspectionSummaryLink = this.baseDef.hasInspectionSummaryLink;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaTop:
        this.baseDef[metadata.parentName][metadata.name] = metadata.value as number;
        if (metadata.parentName === this.metaTitle) {
          this.titleTop = this.units.concatenate('sizeUnit', metadata.value as number);
        }
        if (metadata.parentName === this.metaTable) {
          this.tableTop = this.units.concatenate('sizeUnit', metadata.value as number);
        }
        this.setPreviewValue(metadata.name, metadata.value, metadata.parentName);
        break;
      case this.metaLeft:
        this.baseDef[metadata.parentName][metadata.name] = metadata.value as number;
        if (metadata.parentName === this.metaTitle) {
          this.titleLeft = this.units.concatenate('sizeUnit', metadata.value as number);
        }
        if (metadata.parentName === this.metaTable) {
          this.tableLeft = this.units.concatenate('sizeUnit', metadata.value as number);
        }
        this.setPreviewValue(metadata.name, metadata.value, metadata.parentName);
        break;
      case this.metaWidth:
        this.baseDef[metadata.parentName][metadata.name] = metadata.value as number;
        if (metadata.parentName === this.metaTitle) {
          this.titleWidth = this.getWidth(metadata.parentName);
        }
        if (metadata.parentName === this.metaTable) {
          this.tableWidth = this.getWidth(metadata.parentName);
        }
        this.setPreviewValue(metadata.name, metadata.value, metadata.parentName);
        break;
      case this.metaHeight:
        this.baseDef[metadata.parentName][metadata.name] = metadata.value as number;
        if (metadata.parentName === this.metaTitle) {
          this.titleHeight = this.units.concatenate('sizeUnit', this.baseDef.title.height);
        }
        if (metadata.parentName === this.metaTable) {
          if (this.baseDef.table.height === 0) {
            this.tableHeight = this.getHeight(metadata.parentName);
            this.tableRowHeight = [(this.getHeightNum(metadata.parentName) / this.defaultPageBreakRowCount), 'mm'].join('');
          } else {
            this.tableHeight = this.units.concatenate('sizeUnit', this.baseDef.table.height);
            this.tableRowHeight = [(this.getHeightNum(metadata.parentName) / this.defaultPageBreakRowCount), 'mm'].join('');
          }
        }
        this.setPreviewValue(metadata.name, metadata.value, metadata.parentName);
        break;
      case this.metaZIndex:
        this.baseDef[metadata.parentName][metadata.name] = metadata.value as number;
        if (metadata.parentName === this.metaTitle) {
          this.titleZIndex = metadata.value as number;
        }
        if (metadata.parentName === this.metaTable) {
          this.tableZIndex = metadata.value as number;
        }
        this.setPreviewValue(metadata.name, metadata.value, metadata.parentName);
        break;
      case this.metaBorderColor:
        this.baseDef[metadata.parentName][metadata.name] = metadata.value as string;
        if (metadata.parentName === this.metaTitle) {
          this.titleBorderColor = metadata.value as string;
        }
        if (metadata.parentName === this.metaTable) {
          this.tableBorderColor = metadata.value as string;
        }
        this.setPreviewValue(metadata.name, metadata.value, metadata.parentName);
        break;
      case this.metaBorderStyle:
        this.baseDef[metadata.parentName][metadata.name] = metadata.value as string;
        if (metadata.parentName === this.metaTitle) {
          this.titleBorderStyle = metadata.value as string;
        }
        if (metadata.parentName === this.metaTable) {
          this.tableBorderStyle = metadata.value as string;
        }
        this.setPreviewValue(metadata.name, metadata.value, metadata.parentName);
        break;
      case this.metaBorderWidth:
        this.baseDef[metadata.parentName][metadata.name] = metadata.value as number;
        if (metadata.parentName === this.metaTitle) {
          this.titleBorderWidth = this.units.concatenate('sizeUnit', metadata.value as number);
        }
        if (metadata.parentName === this.metaTable) {
          this.tableBorderWidth = this.units.concatenate('sizeUnit', metadata.value as number);
        }
        this.setPreviewValue(metadata.name, metadata.value, metadata.parentName);
        break;
      case this.metaTextAlign:
        this.baseDef[metadata.parentName][metadata.name] = metadata.value as string;
        if (metadata.parentName === this.metaTitle) {
          this.titleTextAlign = metadata.value as string;
        }
        if (metadata.parentName === this.metaTable) {
          this.tableHeaderHorizontalAlign = metadata.value as string;
        }
        this.setPreviewValue(metadata.name, metadata.value, metadata.parentName);
        break;

      case this.metaBGColor:
        this.baseDef[metadata.parentName][metadata.name] = metadata.value as string;
        this.titleBackgroundColor = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value, metadata.parentName);
        break;
      case this.metaType:
        this.baseDef[metadata.parentName][metadata.name] = metadata.value as string;
        this.titleText = this.getText();
        this.setPreviewValue(metadata.name, metadata.value, metadata.parentName);
        if (metadata.value === 'static') {
          this.setPreviewValue(this.metaDynamicId, null, metadata.parentName);
          this.baseDef[this.metaDynamicId] = null;
          this.setPreviewValue(this.metaEditableId, null, metadata.parentName);
          this.baseDef[this.metaEditableId] = null;
          this.setPreviewValue(this.metaFormat, null, metadata.parentName);
          this.baseDef[this.metaFormat] = null;
        } else if (metadata.value === 'dynamic') {
          this.setPreviewValue(this.metaText, '', metadata.parentName);
          this.baseDef[this.metaText] = '';
          this.setPreviewValue(this.metaEditableId, null, metadata.parentName);
          this.baseDef[this.metaEditableId] = null;
        }
        break;
      case this.metaText:
        this.baseDef[metadata.parentName][metadata.name] = metadata.value as string;
        this.titleText = this.getText();
        this.setPreviewValue(metadata.name, metadata.value, metadata.parentName);
        break;
      case this.metaFormat:
        this.baseDef[metadata.parentName][metadata.name] = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value, metadata.parentName);
        break;
      case this.metaFontSize:
        this.baseDef[metadata.parentName][metadata.name] = metadata.value as number;
        if (metadata.parentName === this.metaTitle) {
          this.titleFontSize = this.units.concatenate('fontSizeUnit', this.baseDef.title.fontSize);
          this.setPreviewValue(metadata.name, metadata.value, metadata.parentName);
        }
        break;
      case this.metaFontFamily:
        this.baseDef[metadata.parentName][metadata.name] = metadata.value as string;
        if (metadata.parentName === this.metaTitle) {
          this.titleFontFamily = this.fonts.getFontFamily(this.baseDef.title);
          this.setPreviewValue(metadata.name, metadata.value, metadata.parentName);
        }
        break;
      case this.metaFontColor:
        this.baseDef[metadata.parentName][metadata.name] = metadata.value as string;
        this.titleFontColor = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value, metadata.parentName);
        break;
      case this.metaFontStyle:
        this.baseDef[metadata.parentName][metadata.name] = metadata.value as string;
        this.titleFontStyle = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value, metadata.parentName);
        break;
      case this.metaFontWeight:
        this.baseDef[metadata.parentName][metadata.name] = metadata.value as string;
        this.titleFontWeight = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value, metadata.parentName);
        break;
      case this.metaVerticalAlign:
        this.baseDef[metadata.parentName][metadata.name] = metadata.value as string;
        this.titleVerticalAlign = metadata.value as string;
        this.titleAlignItems = this.getAlignItems();
        this.setPreviewValue(metadata.name, metadata.value, metadata.parentName);
        break;
      case this.metaIsHorizontal:
        this.baseDef[metadata.parentName][metadata.name] = metadata.value as boolean;
        this.titleIsHorizontal = metadata.value as boolean;
        this.setPreviewValue(metadata.name, metadata.value, metadata.parentName);
        break;
      case this.metaLineHeight:
        this.baseDef[metadata.parentName][metadata.name] = metadata.value as number;
        this.titleLineHeight = this.units.concatenate('sizeUnit', metadata.value as number);
        this.setPreviewValue(metadata.name, metadata.value, metadata.parentName);
        break;
      case this.metaDynamicId:
        this.baseDef[metadata.parentName][metadata.name] = metadata.value as string;
        this.titleText = this.getText();
        this.setPreviewValue(metadata.name, metadata.value, metadata.parentName);
        break;
      case this.metaInputType:
        this.baseDef[metadata.parentName][metadata.name] = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value, metadata.parentName);
        if (metadata.value !== 'select' && metadata.value !== 'multiSelect') {
          this.setPreviewValue(this.metaOptions, '[]', metadata.parentName);
          this.baseDef[metadata.parentName][this.metaOptions] = [];
        }
        if (metadata.value !== 'multiSelect') {
          this.setPreviewValue(this.metaMultiSelectDelimiter, 'comma', metadata.parentName);
          this.baseDef[metadata.parentName][this.metaMultiSelectDelimiter] = 'comma';
        }
        if (metadata.value !== 'radio') {
          this.setPreviewValue(this.metaRadioType, null, metadata.parentName);
          this.baseDef[metadata.parentName][this.metaRadioType] = null;
          this.setPreviewValue(this.metaRadioLabel, null, metadata.parentName);
          this.baseDef[metadata.parentName][this.metaRadioLabel] = null;
          this.setPreviewValue(this.metaValue, null, metadata.parentName);
          this.baseDef[metadata.parentName][this.metaValue] = null;
        }
        break;
      case this.metaEditableId:
        this.baseDef[metadata.parentName][metadata.name] = metadata.value as string;
        this.titleText = this.getText();
        this.setPreviewValue(metadata.name, metadata.value, metadata.parentName);
        break;
      case this.metaRequired:
        const metaDataRequired = metadata.name.split('.');
        if (!this.baseDef[metadata.parentName][metaDataRequired[0]]) {
          this.baseDef[metadata.parentName][metaDataRequired[0]] = { required: false, maxLength: null };
        }
        this.baseDef[metadata.parentName][metaDataRequired[0]][metaDataRequired[1]] = metadata.value as boolean;
        this.setPreviewValue(metadata.name, metadata.value, metadata.parentName);
        break;
      case this.metaMaxLength:
        const metaDataMaxLength = metadata.name.split('.');
        if (!this.baseDef[metadata.parentName][metaDataMaxLength[0]]) {
          this.baseDef[metadata.parentName][metaDataMaxLength[0]] = { required: false, maxLength: null };
        }
        this.baseDef[metadata.parentName][metaDataMaxLength[0]][metaDataMaxLength[1]] = metadata.value as number;
        this.setPreviewValue(metadata.name, metadata.value, metadata.parentName);
        break;
      case this.metaRadioType:
        this.baseDef[metadata.parentName][metadata.name] = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value, metadata.parentName);
        break;
      case this.metaRadioLabel:
        this.baseDef[metadata.parentName][metadata.name] = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value, metadata.parentName);
        break;
      case this.metaOptions:
        this.baseDef[metadata.parentName][metadata.name] = JSON.parse(metadata.value as string);
        this.setPreviewValue(metadata.name, metadata.value, metadata.parentName);
        break;
      case this.metaMultiSelectDelimiter:
        this.baseDef[metadata.parentName][metadata.name] = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value, metadata.parentName);
        break;
      case this.metaLabel:
        this.baseDef[metadata.parentName][metadata.name] = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value, metadata.parentName);
        break;
      case this.metaValue:
        this.baseDef[metadata.parentName][metadata.name] = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value, metadata.parentName);
        break;
      case this.metaInputGroup:
        this.baseDef[metadata.parentName][metadata.name] = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value, metadata.parentName);
        break;
      case this.metaPaddingLeft:
        this.baseDef[metadata.parentName][metadata.name] = metadata.value as number;
        this.titlePaddingLeft = this.units.concatenate('sizeUnit', metadata.value as number);
        this.setPreviewValue(metadata.name, metadata.value, metadata.parentName);
        break;
      case this.metaPaddingBottom:
        this.baseDef[metadata.parentName][metadata.name] = metadata.value as number;
        this.titlePaddingBottom = this.units.concatenate('sizeUnit', metadata.value as number);
        this.setPreviewValue(metadata.name, metadata.value, metadata.parentName);
        break;
      case this.metaPaddingRight:
        this.baseDef[metadata.parentName][metadata.name] = metadata.value as number;
        this.titlePaddingRight = this.units.concatenate('sizeUnit', metadata.value as number);
        this.setPreviewValue(metadata.name, metadata.value, metadata.parentName);
        break;
      case this.metaPaddingTop:
        this.baseDef[metadata.parentName][metadata.name] = metadata.value as number;
        this.titlePaddingTop = this.units.concatenate('sizeUnit', metadata.value as number);
        this.setPreviewValue(metadata.name, metadata.value, metadata.parentName);
        break;
      case this.metaTableHeaderHeight:
        this.baseDef[metadata.parentName][metadata.name] = metadata.value as number;
        this.tableHeaderHeight = this.units.concatenate('sizeUnit', this.baseDef.table.headerHeight);
        this.setPreviewValue(metadata.name, metadata.value, metadata.parentName);
        break;
      case this.metaTableHeaderFontSize:
        this.baseDef[metadata.parentName][metadata.name] = metadata.value as number;
        this.tableHeaderFontSize = this.units.concatenate('fontSizeUnit', this.baseDef.table.headerFontSize);
        this.setPreviewValue(metadata.name, metadata.value, metadata.parentName);
        break;
      case this.metaTableHeaderFontFamily:
        this.baseDef[metadata.parentName][metadata.name] = metadata.value as string;
        this.tableHeaderFontFamily = this.baseDef.table.headerFontFamily;
        this.setPreviewValue(metadata.name, metadata.value, metadata.parentName);
        break;
      case this.metaTableHeaderBackgroundColor:
        this.baseDef[metadata.parentName][metadata.name] = metadata.value as string;
        this.tableHeaderBackgroundColor = this.baseDef.table.headerBackgroundColor;
        this.setPreviewValue(metadata.name, metadata.value, metadata.parentName);
        break;
      case this.metaTableHasBorderTop:
        this.baseDef[metadata.parentName][metadata.name] = metadata.value as boolean;
        this.tableHasBorderTop = this.baseDef.table.hasBorderTop;
        this.setPreviewValue(metadata.name, metadata.value, metadata.parentName);
        break;
      case this.metaTableHasBorderBottom:
        this.baseDef[metadata.parentName][metadata.name] = metadata.value as boolean;
        this.tableHasBorderBottom = this.baseDef.table.hasBorderBottom;
        this.setPreviewValue(metadata.name, metadata.value, metadata.parentName);
        break;
      case this.metaTableHasBorderLeft:
        this.baseDef[metadata.parentName][metadata.name] = metadata.value as boolean;
        this.tableHasBorderLeft = this.baseDef.table.hasBorderLeft;
        this.setPreviewValue(metadata.name, metadata.value, metadata.parentName);
        break;
      case this.metaTableHasBorderRight:
        this.baseDef[metadata.parentName][metadata.name] = metadata.value as boolean;
        this.tableHasBorderRight = this.baseDef.table.hasBorderRight;
        this.setPreviewValue(metadata.name, metadata.value, metadata.parentName);
        break;
      case this.metaTableHasRowBackground:
        this.baseDef[metadata.parentName][metadata.name] = metadata.value as boolean;
        this.tableHasRowBackground = this.baseDef.table.hasRowBackground;
        this.setPreviewValue(metadata.name, metadata.value, metadata.parentName);
        break;
      case this.metaTableRowBackgroundColor:
        this.baseDef[metadata.parentName][metadata.name] = metadata.value as string;
        this.tableRowBackgroundColor = this.baseDef.table.rowBackgroundColor;
        this.setPreviewValue(metadata.name, metadata.value, metadata.parentName);
        break;
      case this.metaDataMode:
        this.baseDef[metadata.parentName][metadata.name] = metadata.value as number;
        this.dataMode = this.baseDef.table.dataMode;
        this.setPreviewValue(metadata.name, metadata.value, metadata.parentName);
        break;
      case this.metaTableColumns:
        this.baseDef[metadata.parentName][metadata.name] = metadata.value as TableColumn[];
        this.tableColumns = this.baseDef.table.columns;
        this.setPreviewValue(metadata.name, metadata.value, metadata.parentName);
        break;
    }
  }

  /**
   * 他インスタンスの更新に伴い、自インスタンスの更新を行う関数を設定する
   * @param subscribers 連鎖更新を行う関数を格納する配列
   */
  protected setChainUpdateFunction(subscribers: Array<Subscription>) {
    subscribers.push(
      this.pageMargin.onUpdateLeft.subscribe((metadata: UpdateMetadata) => {
        // paddingLeftの再設定
        this.tableWidth = this.getWidth('table');
        // tableWidthの再設定
        // →tableWidthプロパティの値が変わる＝要Saveな状態にする必要があるので、
        //  updateProperties()経由でプロパティを設定
        this.updateProperties({
          id: this.getId(),
          name: this.metaWidth,
          parentName: this.metaTable,
          value: this.getWidthNum('table'),
          oldValue: this.baseDef.table.width
        });
      })
    );
    subscribers.push(
      this.pageMargin.onUpdateRight.subscribe((metadata: UpdateMetadata) => {
        // paddingRightの再設定
        this.tableWidth = this.getWidth('table');
        // tableWidthの再設定
        // →tableWidthプロパティの値が変わる＝要Saveな状態にする必要があるので、
        //  updateProperties()経由でプロパティを設定
        this.updateProperties({
          id: this.getId(),
          name: this.metaWidth,
          parentName: this.metaTable,
          value: this.getWidthNum('table'),
          oldValue: this.baseDef.table.width
        });
      })
    );
    subscribers.push(
      this.paperSize.onUpdatePaperSize.subscribe((metadata: UpdateMetadata) => {
        // tableWidth、tableHeightの再設定
        this.tableWidth = this.getWidth('table');
        this.tableHeight = this.getHeight('table');
        // tableWidthの再設定
        // →tableWidthプロパティの値が変わる＝要Saveな状態にする必要があるので、
        //  updateProperties()経由でプロパティを設定
        this.updateProperties({
          id: this.getId(),
          name: this.metaWidth,
          parentName: this.metaTable,
          value: this.getWidthNum('table'),
          oldValue: this.baseDef.table.width
        });
      })
    );
    subscribers.push(
      this.units.onUpdateSizeUnit.subscribe((metadata: UpdateMetadata) => {
        // サイズの単位が変更された時、自身が持つサイズの値を再計算する
        // 自身が持つサイズのプロパティに新しい値を設定する＝未保存状態にする必要があるため
        // updateProperties()経由で新しい値を設定する
        this.updateProperties({
          id: this.getId(),
          name: this.metaTop,
          parentName: this.metaTable,
          value: this.convertUnit(this.baseDef[this.metaTable][this.metaTop], metadata.oldValue as string, metadata.value as string),
          oldValue: this.baseDef[this.metaTable][this.metaTop]
        });
        this.updateProperties({
          id: this.getId(),
          name: this.metaLeft,
          parentName: this.metaTable,
          value: this.convertUnit(this.baseDef[this.metaTable][this.metaLeft], metadata.oldValue as string, metadata.value as string),
          oldValue: this.baseDef[this.metaTable][this.metaLeft]
        });
        this.updateProperties({
          id: this.getId(),
          name: this.metaWidth,
          parentName: this.metaTable,
          value: this.convertUnit(this.baseDef[this.metaTable][this.metaWidth], metadata.oldValue as string, metadata.value as string),
          oldValue: this.baseDef[this.metaTable][this.metaWidth]
        });
        this.updateProperties({
          id: this.getId(),
          name: this.metaHeight,
          parentName: this.metaTable,
          value: this.convertUnit(this.baseDef[this.metaTable][this.metaHeight], metadata.oldValue as string, metadata.value as string),
          oldValue: this.baseDef[this.metaTable][this.metaHeight]
        });
      })
    );
  }
  private getHeightNum(parentName: string): number {
    let rtn = this.paperSize.getHeightNum();
    rtn = this.pageMargin.getPaddedHeightNum(rtn);
    if (parentName === 'table') {
      rtn = rtn - this.header.getHeightNum() - this.footer.getHeightNum() - (this.baseDef.title.height + this.baseDef.table.headerHeight + this.baseDef.table.y * 2);
    }
    rtn = Math.floor(rtn);
    return rtn;
  }
  private getHeight(parentName: string): string {
    return [this.getHeightNum(parentName), 'mm'].join('');
  }
  private getWidthNum(parentName: string): number {
    let rtn = this.paperSize.getWidthNum();
    let width;
    if (parentName === 'title') {
      width = this.baseDef.title.width;
    }
    else if (parentName === 'table') {
      width = this.baseDef.table.width
    }
    if (width <= rtn) {
      return width;
    }

    let x;
    if (parentName === 'title') {
      x = this.baseDef.title.x;
    }
    else if (parentName === 'table') {
      x = this.baseDef.table.x
    }
    if (this.type === ElementType.Summary) {
      // 検査サマリの横幅を設定
      rtn = this.pageMargin.getPaddedWidthNum(rtn) - (x * 2);
    } else {
      // 検査画像の横幅を設定（既存のJsonの値を変えないように調整）
      rtn = this.pageMargin.getPaddedWidthNum(rtn) - (x * 2) - 5;
    }
    rtn = Math.floor(rtn);
    return rtn;
  }
  private getWidth(parentName: string): string {
    return [this.getWidthNum(parentName), 'mm'].join('');
  }
  private getThWidth(column: TableColumn): string {
    return [this.getWidthNum('table') * (column.widthRatio / 100), 'mm'].join('');
  }

  private getText(): string {
    let rtnText = '';
    switch (this.baseDef.title.type) {
      case 'static':
        rtnText = this.baseDef.title.text;
        break;
      case 'dynamic':
        rtnText = this.baseDef.title.dynamicDataField ? '[' + this.baseDef.title.dynamicDataField + ']' : '[' + this.translate.instant('objects.dynamicText') + ']';
        break;
      case 'dynamic-editable':
        rtnText = this.baseDef.title.editableId ? '{' + this.baseDef.title.editableId + '}' : '{' + this.translate.instant('objects.editableText') + '}';
        break;
    }
    return rtnText;
  }

  private getJustifyContent(): string {
    let rtn: string;
    switch (this.baseDef.title.horizontalAlign) {
      case 'left':
        rtn = 'flex-start';
        break;
      case 'center':
        rtn = 'center';
        break;
      case 'right':
        rtn = 'flex-end';
        break;
    }
    return rtn;
  }

  private getAlignItems(): string {
    let rtn: string;
    switch (this.baseDef.title.verticalAlign) {
      case 'top':
        rtn = 'flex-start';
        break;
      case 'middle':
        rtn = 'center';
        break;
      case 'bottom':
        rtn = 'flex-end';
        break;
    }
    return rtn;
  }

  public arrayNumberLength(): any[] {
    return [...Array(this.defaultPageBreakRowCount)];
  }

  public getMetadataId() {
    return this.baseDef[this.metaId];
  }
}
